import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { isEmpty, capitalize } from 'lodash';
import { COLORS } from 'Components/utils/color/colors';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { Button } from 'Components/frameworks/button/Button';
import { Countdown } from 'Components/frameworks/countdown/Countdown';
import { Heading } from 'Components/frameworks/heading/Heading';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { PopOver } from 'Components/frameworks/pop-over/PopOver';
import { Text } from 'Components/frameworks/text/Text';
import { themes } from './InfoBanner.themes';
const positions = {
  left: 'start-0',
  right: 'end-0',
  center: 'inset-x-0',
};
type Props = {
  className?: string;
  containerClassName?: string;
  contentClassName?: string;
  color: Colors;
  countdown?: {
    dateTimeEnd: string;
    days?: string;
    hours?: string;
    minutes?: string;
    seconds?: string;
  };
  iconName?: string;
  illustration?: string;
  popin?: {
    buttonLabel?: string;
    color?: Colors;
    image?: string;
    text?: string;
    title: string;
    subtitle?: string;
    url?: string;
  };
  popinPosition?: string;
  subtitle?: string;
  theme?: keyof typeof themes;
  title?: string;
};
export const InfoBanner = ({
  className,
  containerClassName = 'w-800',
  contentClassName,
  color = COLORS.BLACK,
  countdown,
  iconName,
  illustration,
  popin,
  popinPosition = 'left',
  subtitle,
  title,
  theme = 'default',
}: Props) => {
  const { trackClickEvent } = useTrackEvent();
  const { isDesktop } = useResponsive();
  const hasModal = !!popin && (!!popin?.title || !!popin?.text || !!popin?.subtitle);
  const hasCountdown = !isEmpty(countdown) && countdown.dateTimeEnd;
  const hasIcon = Boolean(illustration || iconName);
  const { thDescriptionBloc, thPopinContent, thPopinContentImage, thPopinColor, thPopinTextColor } =
    themes({
      color,
    })[theme];

  if (!title && !subtitle && !hasIcon && !hasCountdown) {
    return null;
  }

  const content = (
    <div className={classnames('text-initial flex', contentClassName)}>
      {hasIcon && (
        <div className={`border-${color} h-56 shrink-0 overflow-hidden rounded-4 border-1`}>
          {illustration ? (
            <img alt="" className="h-full" src={illustration} />
          ) : (
            <Icon className={`text-${color} p-5`} name={iconName} width="3.5rem" />
          )}
        </div>
      )}
      {hasCountdown && (
        <div
          className={`bg-white border-${color} flex h-56 grow flex-col items-center justify-center overflow-hidden rounded-4 border-1 sm:order-1 sm:grow-0 text-${color}`}
        >
          <Countdown {...countdown} dateTimeEnd={countdown?.dateTimeEnd} theme="infoBanner" />
        </div>
      )}
      {(title || subtitle) && (
        <div className={classnames(thDescriptionBloc, 'h-56')}>
          <h6 className="text-10">{subtitle}</h6>
          <h5 className="font-brand text-16 font-normal underline">{title}</h5>
        </div>
      )}
    </div>
  );
  const imageProps = isDesktop
    ? {
        dimensions: {
          w: 300,
          h: 170,
        },
      }
    : {
        dontChangeAnything: true,
      };
  const tooltipContent = hasModal ? (
    <div className={thPopinContent}>
      {popin?.image && (
        <div className={classnames(thPopinContentImage, 'lg:h-220')}>
          <Image
            className="h-full overflow-hidden rounded-12"
            imageUrl={popin.image}
            {...imageProps}
          />
        </div>
      )}
      <div className="p-20">
        {popin?.title && (
          <Heading
            className={classnames(
              'font-brand text-24 font-normal leading-tight',
              `text-${thPopinColor}`,
            )}
            hLevel={3}
            theme="none"
          >
            {popin.title}
          </Heading>
        )}
        {popin?.subtitle && (
          <Heading className="text-14 font-bold leading-tight" hLevel={4} theme="none">
            {popin.subtitle}
          </Heading>
        )}
        {popin?.text && (
          <Text className="py-10 text-14 leading-tight lg:leading-loose" text={popin.text} />
        )}
        {popin?.url && (
          <Button color={thPopinTextColor} colorBg={thPopinColor} url={popin.url}>
            {popin.buttonLabel}
          </Button>
        )}
      </div>
    </div>
  ) : null;
  const position = popinPosition === 'left' ? 'left' : 'right';
  return (
    <div className={classnames('relative isolate', className)}>
      {hasModal ? (
        <PopOver
          ariaLabel={popin?.title ? popin.title : ''}
          className="flex justify-end"
          containerClassName={classnames(
            `z-3 border-1 border-${color} ${positions[popinPosition]}`,
            containerClassName,
          )}
          contentClassName={`arrow-bg arrow-bg-${color} arrow-up${capitalize(position)}`}
          content={tooltipContent}
          hasCloseButton
          onClick={() =>
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_INFOBANNER, {
              detail_click: title,
            })
          }
          theme="infoBanner"
        >
          {content}
        </PopOver>
      ) : (
        content
      )}
    </div>
  );
};
